import React from 'react';
import { Award, Shield, CheckCircle, Users, FileText } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { certifications } from '../mock/data';

const Certifications = () => {
  const certificationDetails = [
    {
      icon: <Award className="h-12 w-12 text-yellow-500" />,
      name: "Basic Food Hygiene",
      category: "Food Safety Foundation",
      description: "Essential certification ensuring proper food handling and hygiene practices across all operations",
      requirements: [
        "Understanding of foodborne illnesses and contamination",
        "Proper food storage and temperature control",
        "Personal hygiene and sanitation practices",
        "Cleaning and sanitizing procedures"
      ],
      validity: "3 years",
      issuedBy: "Singapore Food Agency (SFA)"
    },
    {
      icon: <Shield className="h-12 w-12 text-green-500" />,
      name: "Advanced Food Hygiene Officer",
      category: "Management Level",
      description: "Advanced certification for food safety management, supervision, and regulatory compliance",
      requirements: [
        "HACCP principles and implementation",
        "Food safety management systems",
        "Regulatory compliance and auditing",
        "Staff training and supervision"
      ],
      validity: "5 years",
      issuedBy: "Singapore Food Agency (SFA)"
    },
    {
      icon: <Users className="h-12 w-12 text-blue-500" />,
      name: "Eatsafe SG (IDDSI)",
      category: "Specialized Certification",
      description: "International Dysphagia Diet Standardisation Initiative compliance for therapeutic diet preparation",
      requirements: [
        "IDDSI framework understanding",
        "Texture modification techniques",
        "Nutritional assessment for dysphagia",
        "Safe feeding practices"
      ],
      validity: "2 years",
      issuedBy: "Agency for Integrated Care (AIC)"
    }
  ];

  const complianceAreas = [
    {
      title: "Ministry of Health (MOH) Compliance",
      description: "Full adherence to Singapore's health ministry standards for food safety and public health protection",
      icon: <FileText className="h-8 w-8 text-red-500" />
    },
    {
      title: "Singapore Food Agency (SFA) Standards",
      description: "Compliance with national food safety regulations and licensing requirements",
      icon: <Shield className="h-8 w-8 text-green-500" />
    },
    {
      title: "International Standards",
      description: "Adherence to international food safety and quality management standards",
      icon: <Award className="h-8 w-8 text-blue-500" />
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-red-600 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Certifications & Compliance</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Maintaining the highest standards through rigorous certifications, 
              continuous training, and unwavering commitment to regulatory compliance.
            </p>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our Commitment to Excellence
            </h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              At Teng Food Holdings, we maintain the highest standards of food safety and quality 
              through comprehensive certification programs and strict adherence to regulatory requirements. 
              Our team of certified professionals ensures that every aspect of our operations meets 
              or exceeds industry standards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {complianceAreas.map((area, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    {area.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{area.title}</h3>
                  <p className="text-gray-600">{area.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Certifications */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Professional Certifications
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our staff holds various professional certifications ensuring expert knowledge 
              in food safety management and specialized diet preparation.
            </p>
          </div>

          <div className="space-y-12">
            {certificationDetails.map((cert, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                    {/* Icon and Basic Info */}
                    <div className="text-center lg:text-left">
                      <div className="flex justify-center lg:justify-start mb-4">
                        {cert.icon}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{cert.name}</h3>
                      <div className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-4">
                        {cert.category}
                      </div>
                      <p className="text-gray-600 mb-4">{cert.description}</p>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-center lg:justify-start items-center space-x-2">
                          <span className="font-medium text-gray-700">Validity:</span>
                          <span className="text-gray-600">{cert.validity}</span>
                        </div>
                        <div className="flex justify-center lg:justify-start items-center space-x-2">
                          <span className="font-medium text-gray-700">Issued by:</span>
                          <span className="text-gray-600">{cert.issuedBy}</span>
                        </div>
                      </div>
                    </div>

                    {/* Requirements */}
                    <div className="lg:col-span-2">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Key Requirements & Competencies</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {cert.requirements.map((requirement, reqIndex) => (
                          <div key={reqIndex} className="flex items-start space-x-3">
                            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-600">{requirement}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Continuous Training */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Continuous Professional Development
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  We believe that maintaining certifications is just the beginning. Our commitment 
                  to excellence requires ongoing professional development, regular training updates, 
                  and staying current with evolving industry standards and best practices.
                </p>
                <p>
                  All our certified staff participate in regular refresher courses, attend industry 
                  seminars, and undergo periodic assessments to ensure their knowledge and skills 
                  remain current and relevant.
                </p>
                <p>
                  This continuous learning approach ensures that our team is always equipped with 
                  the latest knowledge in food safety, therapeutic nutrition, and regulatory compliance.
                </p>
              </div>
            </div>

            <div>
              <img 
                src="https://images.unsplash.com/photo-1563468304224-1fc761a1cbb5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxjb21tZXJjaWFsJTIwa2l0Y2hlbnxlbnwwfHx8fDE3NjAwMDM3ODB8MA&ixlib=rb-4.1.0&q=85"
                alt="Professional Training"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Certification Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Benefits of Our Certified Team
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Enhanced Food Safety",
                description: "Reduced risk of foodborne illnesses and contamination",
                icon: <Shield className="h-8 w-8 text-green-500" />
              },
              {
                title: "Regulatory Compliance",
                description: "Full adherence to local and international standards",
                icon: <FileText className="h-8 w-8 text-blue-500" />
              },
              {
                title: "Quality Assurance",
                description: "Consistent quality across all our food products",
                icon: <Award className="h-8 w-8 text-yellow-500" />
              },
              {
                title: "Customer Confidence",
                description: "Trust and reliability in our food safety practices",
                icon: <Users className="h-8 w-8 text-purple-500" />
              }
            ].map((benefit, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Questions About Our Certifications?
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
            Contact our team to learn more about our certification programs and 
            how our expertise can benefit your organization.
          </p>
          <button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors">
            Contact Our Certified Team
          </button>
        </div>
      </section>
    </div>
  );
};

export default Certifications;